import React, { useState } from "react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CheckCircle, Domain, Error } from "@mui/icons-material";
import { acme } from "../../theme/acmeTheme";
import { Table, MenuItem, TableActionsMenu, TableActionsMenuButton, destructiveMenuItemSx, stopTableRowClick } from "../ui";
import { CircularIndicator } from "../RatingIndicators";
import { getRowColor } from "./domainsUtils";

const DomainsTable = ({
  domains,
  refreshingRatings,
  checkingDomains,
  onRefreshDomainRatings,
  onOpenDetails,
  onCheckAllRecords,
  onOpenConfirmDelete,
}) => {
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, domain: null });

  const openActionMenu = (event, domain) => {
    event.stopPropagation();
    setActionMenu({ anchorEl: event.currentTarget, domain });
  };

  const closeActionMenu = () => {
    setActionMenu({ anchorEl: null, domain: null });
  };

  const handleActionMenuItem = (action) => {
    const domain = actionMenu.domain;
    if (!domain) return;
    closeActionMenu();

    if (action === "details") {
      onOpenDetails(domain);
      return;
    }
    if (action === "checkDns") {
      onCheckAllRecords(domain._id);
      return;
    }
    if (action === "delete") {
      onOpenConfirmDelete(domain);
    }
  };

  return (
    <>
      <Table containerSx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Domain</TableCell>
            <TableCell align="center">Trust Rating</TableCell>
            <TableCell align="center">Security Score</TableCell>
            <TableCell align="center">SPF Status</TableCell>
            <TableCell align="center">DKIM Status</TableCell>
            <TableCell align="center">DMARC Status</TableCell>
            <TableCell align="center">BIMI Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.isArray(domains) && domains.length > 0 ? (
            domains.map((d) => (
              <TableRow
                key={d._id}
                sx={{
                  backgroundColor: getRowColor(d.domainRating || 0),
                  "&:hover": {
                    backgroundColor: getRowColor(d.domainRating || 0),
                    opacity: 0.8,
                  },
                }}
              >
                <TableCell>{d.domain}</TableCell>

                <TableCell align="center">
                  <CircularIndicator
                    value={d.domainRating || 0}
                    label="Trust"
                    size={50}
                    onRefresh={() => onRefreshDomainRatings(d._id)}
                    loading={refreshingRatings[d._id]}
                  />
                </TableCell>

                <TableCell align="center">
                  <CircularIndicator
                    value={d.securityScore || 0}
                    label="Security"
                    size={50}
                    onRefresh={() => onRefreshDomainRatings(d._id)}
                    loading={refreshingRatings[d._id]}
                  />
                </TableCell>

                <TableCell align="center">
                  {d.spfStatus ? (
                    <CheckCircle sx={{ color: acme.colors.brand }} />
                  ) : (
                    <Error sx={{ color: acme.colors.destructive }} />
                  )}
                </TableCell>

                <TableCell align="center">
                  {d.dkimStatus ? (
                    <CheckCircle sx={{ color: acme.colors.brand }} />
                  ) : (
                    <Error sx={{ color: acme.colors.destructive }} />
                  )}
                </TableCell>

                <TableCell align="center">
                  {d.dmarcStatus ? (
                    <CheckCircle sx={{ color: acme.colors.brand }} />
                  ) : (
                    <Error sx={{ color: acme.colors.destructive }} />
                  )}
                </TableCell>

                <TableCell align="center">
                  {d.bimiStatus ? (
                    <CheckCircle sx={{ color: acme.colors.brand }} />
                  ) : (
                    <Error sx={{ color: acme.colors.destructive }} />
                  )}
                </TableCell>

                <TableCell onClick={stopTableRowClick}>
                  <TableActionsMenuButton
                    ariaLabel="Domain actions"
                    onClick={(event) => openActionMenu(event, d)}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                <Domain sx={{ fontSize: 64, color: acme.colors.mutedForegroundSolid, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: acme.colors.foreground, mb: 1 }}>
                  No domains added yet
                </Typography>
                <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                  Add a domain above to configure SPF, DKIM, DMARC and BIMI records.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TableActionsMenu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={closeActionMenu}
      >
        <MenuItem onClick={() => handleActionMenuItem("details")}>
          Domain details
        </MenuItem>
        <MenuItem
          disabled={checkingDomains[actionMenu.domain?._id]}
          onClick={() => handleActionMenuItem("checkDns")}
        >
          {checkingDomains[actionMenu.domain?._id] ? "Checking..." : "Check DNS"}
        </MenuItem>
        <MenuItem
          onClick={() => handleActionMenuItem("delete")}
          sx={destructiveMenuItemSx}
        >
          Delete
        </MenuItem>
      </TableActionsMenu>
    </>
  );
};

export default DomainsTable;
